import React, { Dispatch, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { RootState, actions } from "../../store";
import { ThunkDispatch } from "redux-thunk";
import { Action, AnyAction } from "redux";
import { chevronBack, saveOutline, mailOutline } from "ionicons/icons";
import TAXI_PARAMS from "../../utils/build";
import { LoginWithActivationKeyInfo } from "../../webServer/controllers/commonWeb/types/type";
import { UserType } from "../../webServer/controllers/commonWeb/types/type";
import { useTranslation } from "react-i18next";
import {
  IonModal,
  IonButton,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonSpinner,
  IonText,
} from "@ionic/react";
import "./ActivationKeyModal.scss";

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> & {
    keyModal: boolean;
    keyModalClose: () => void;
  };

const ActivationKeyModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { keyModal, keyModalClose, loginWithActivationKeyAction, error } = props;
  const [keyValue, setKeyValue] = useState("");

  const modalConfig = useMemo(() => ({
    header: t("confirmations"),
    keyLabel: t("personalKey"),
    saveText: t("save"),
    confirmationText: t("confirmationKey"), 
  }), [t]);

  const handleConfirmClick = useCallback(() => {
    const parameters: LoginWithActivationKeyInfo = {
      userType: UserType.customer,
      licenceKey: TAXI_PARAMS.WEBSERVER_APIKEY || '',
      activationKey: keyValue,
    };

    loginWithActivationKeyAction(parameters);
  }, [keyValue, loginWithActivationKeyAction]);

  const handleInputChange = useCallback((e: CustomEvent) => {
    setKeyValue(e.detail.value || "");
  }, []);

  const handleClose = useCallback(() => {
    keyModalClose();
  }, [keyModalClose]);

  const isValidInput = useMemo(() => 
    keyValue.trim().length > 0, 
    [keyValue]
  );

  if (!keyModal) return null;

  return (
    <IonModal isOpen={keyModal}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{modalConfig.header}</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={handleClose} fill="clear">
              <IonIcon icon={chevronBack} slot="icon-only" />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton fill="clear">
              <IonIcon icon={mailOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent className="confirm">
        <IonList>
          <div className="confirm__title">{modalConfig.confirmationText}:</div> {/* Исправлено: confirmationText */}
          
          <div className="confirm__spinner">
            <IonSpinner name="lines" color="primary" />
          </div>
          
          <IonItem className="confirm__item">
            <IonLabel position="stacked">{modalConfig.keyLabel}</IonLabel>
            <IonInput
              value={keyValue}
              onIonInput={handleInputChange}
              clearInput
              placeholder={t("enterActivationKey")}
              autocapitalize="characters"
            />
          </IonItem>
          
          {error && (
            <IonText color="danger" className="errorMessage">
              {error}
            </IonText>
          )}
          
          <div className="confirm__btnRow">
            <IonButton 
              onClick={handleConfirmClick}
              disabled={!isValidInput}
              expand="block"
            >
              <IonIcon slot="start" icon={saveOutline} />
              {modalConfig.saveText}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: state.registration.error,
  activationKeyOpen: state.registration.activationKeyOpen,
});

const mapDispatchToProps = (
  dispatch: Dispatch<Action> & ThunkDispatch<any, any, AnyAction>
) => ({
  loginWithActivationKeyAction: (parameter: LoginWithActivationKeyInfo) =>
    dispatch(actions.registration.loginWithActivationKeyAction(parameter)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(ActivationKeyModal));